import { Building2, Home, MessageCircle, Settings, Users, BarChart, ChevronDown, Plus, LogOut } from "lucide-react";
import { useState } from "react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { Link, useLocation, useNavigate, matchPath } from "react-router-dom";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { VersionDisplay } from "@/components/ui/version-display";
import { Skeleton } from "@/components/ui/skeleton";
import { logger } from "@/utils/logger";

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { 
    currentCompany, 
    companyPersonas, 
    hasMultipleCompanies, 
    companies, 
    switchCompany,
    isLoadingCompanies
  } = useWorkspace();
  
  // State for optimistic UI during company switching
  const [switchingToCompanyId, setSwitchingToCompanyId] = useState<string | null>(null);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/auth");
      toast.success("Signed out successfully");
    } catch (error) {
      logger.error("Error signing out:", error);
      toast.error("Error signing out");
    }
  };

  const handleCompanySwitch = async (companyId: string) => {
    if (companyId === currentCompany?.id) return;
    
    // Optimistic UI: Show switching state immediately
    setSwitchingToCompanyId(companyId);
    
    try {
      await switchCompany(companyId);
      // Success - the UI will update automatically via context
    } catch (error) {
      logger.error("Error switching company:", error);
      toast.error("Failed to switch company");
    } finally {
      setSwitchingToCompanyId(null);
    }
  };

  const handleNewChat = () => {
    if (currentCompany) {
      navigate(`/w/${currentCompany.slug}/chat`);
    } else {
      toast.error("Please select a project first");
      navigate("/companies");
    }
  };

  // Generate navigation items based on current company
  const getNavigationItems = () => {
    if (!currentCompany) {
      // Limited navigation when no company is selected
      return {
        main: [
          {
            title: "Select Project",
            url: "/companies",
            icon: Building2,
            highlight: true,
          }
        ],
        settings: [
          {
            title: "Settings",
            url: "/settings",
            icon: Settings,
          }
        ]
      };
    }

    // Full navigation when company is selected
    const baseUrl = `/w/${currentCompany.slug}`;
    return {
      main: [
        {
          title: "Dashboard",
          url: `${baseUrl}/dashboard`,
          icon: Home,
        },
        {
          title: "Marketing Mary",
          url: `${baseUrl}/chat`,
          icon: MessageCircle,
        },
        {
          title: "Personas",
          url: `${baseUrl}/personas`,
          icon: Users,
          badge: companyPersonas?.length > 0 ? companyPersonas.length : undefined,
          children: (companyPersonas ?? []).slice(0, 5).map(persona => ({
            title: persona.name,
            subtitle: persona.job_title,
            url: `${baseUrl}/chat/p/${persona.id}`,
          })),
        },
        {
          title: "Analysis",
          url: `${baseUrl}/analysis`,
          icon: BarChart,
        },
      ],
      settings: [
        {
          title: hasMultipleCompanies ? "Projects" : "Project",
          url: "/companies",
          icon: Building2,
        },
        {
          title: "Settings",
          url: "/settings",
          icon: Settings,
        },
      ]
    };
  };

  const navItems = getNavigationItems();

  const isActiveUrl = (itemUrl: string) => {
    // Special case for "Select Project" highlight
    if (itemUrl === "/companies" && !currentCompany) {
      return true;
    }
    
    // Use react-router's matchPath for robust matching
    const match = matchPath(
      { path: itemUrl, end: false },
      location.pathname
    );
    
    // For workspace routes, also check if the base paths match after company slug
    if (!match && location.pathname.startsWith('/w/') && itemUrl.startsWith('/w/')) {
      // Extract the path after company slug for both URLs
      const currentPathParts = location.pathname.split('/').slice(3);
      const itemPathParts = itemUrl.split('/').slice(3);
      
      // Check if the item path matches the beginning of the current path
      if (itemPathParts.length > 0 && currentPathParts.length >= itemPathParts.length) {
        return itemPathParts.every((part, index) => part === currentPathParts[index]);
      }
    }
    
    return !!match;
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <Link 
          to={currentCompany ? `/w/${currentCompany.slug}/dashboard` : "/companies"} 
          className="flex items-center justify-center lg:justify-start space-x-2 px-2 py-2"
        >
          <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shrink-0">
            <span className="text-white font-bold text-sm">MM</span>
          </div>
          <span className="font-bold text-xl group-data-[collapsible=icon]:hidden">Marketing Mary</span>
        </Link>
      </SidebarHeader>

      <SidebarContent>
        {/* Company Selector */}
        <div className="px-2 py-3 border-b">
          {isLoadingCompanies ? (
            <Skeleton className="h-10 w-full" />
          ) : currentCompany ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="secondary"
                  className="w-full justify-between h-auto py-3 px-3"
                  disabled={switchingToCompanyId !== null}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Building2 className="h-5 w-5 shrink-0 text-primary" />
                    <div className="text-left min-w-0 group-data-[collapsible=icon]:hidden">
                      <div className="font-semibold truncate">
                        {switchingToCompanyId 
                          ? companies.find(c => c.id === switchingToCompanyId)?.name || currentCompany.name
                          : currentCompany.name
                        }
                      </div>
                      {hasMultipleCompanies && (
                        <div className="text-xs opacity-80">
                          {switchingToCompanyId ? "Switching..." : "Current Project"}
                        </div>
                      )}
                    </div>
                  </div>
                  {hasMultipleCompanies && <ChevronDown className="h-4 w-4 shrink-0 ml-2 group-data-[collapsible=icon]:hidden" />}
                </Button>
              </DropdownMenuTrigger>
              {hasMultipleCompanies && (
                <DropdownMenuContent align="start" className="w-[--radix-dropdown-menu-trigger-width]">
                  <DropdownMenuLabel>Switch Project</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {companies.map((company) => {
                    const isSwitchingTo = switchingToCompanyId === company.id;
                    const isCurrent = company.id === currentCompany.id && !switchingToCompanyId;
                    
                    return (
                      <DropdownMenuItem
                        key={company.id}
                        onClick={() => handleCompanySwitch(company.id)}
                        className={cn(
                          'cursor-pointer',
                          isCurrent && 'bg-accent',
                          isSwitchingTo && 'bg-primary/10 animate-pulse'
                        )}
                        disabled={switchingToCompanyId !== null}
                      >
                        <Building2 className="mr-2 h-4 w-4" />
                        <span className="truncate">{company.name}</span>
                        {isSwitchingTo && (
                          <span className="ml-auto text-xs opacity-70">Switching...</span>
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/companies" className="cursor-pointer">
                      <Plus className="mr-2 h-4 w-4" />
                      <span>Add New Project</span>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              )}
            </DropdownMenu>
          ) : (
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-3 px-3 border-dashed"
              onClick={() => navigate('/companies')}
            >
              <Building2 className="h-5 w-5 mr-2 opacity-60 group-data-[collapsible=icon]:mr-0" />
              <span className="opacity-60 group-data-[collapsible=icon]:hidden">Select a Project</span>
            </Button>
          )}
        </div>

        {/* Navigation Menu */}
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.main.map((item) => {
                const isActive = isActiveUrl(item.url);
                const hasChildren = item.children && item.children.length > 0;
                const ItemIcon = item.icon;
                
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton 
                      asChild 
                      isActive={isActive} 
                      tooltip={item.title}
                      className={cn(item.highlight && "bg-primary/10 hover:bg-primary/20")}
                    >
                      <Link to={item.url} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <ItemIcon className="mr-3 h-5 w-5" />
                          <span>{item.title}</span>
                        </div>
                        {item.badge && (
                          <Badge variant="secondary" className="ml-auto">
                            {item.badge}
                          </Badge>
                        )}
                      </Link>
                    </SidebarMenuButton>
                    {hasChildren && (
                      <SidebarMenuSub>
                        {item.children.map((child) => (
                          <SidebarMenuSubItem key={child.url}>
                            <SidebarMenuSubButton asChild isActive={isActiveUrl(child.url)}>
                              <Link to={child.url} className="text-sm">
                                <div className="flex flex-col gap-0.5 py-0.5">
                                  <div className="leading-tight">{child.title}</div>
                                  {child.subtitle && (
                                    <div className="text-xs opacity-80 leading-tight">{child.subtitle}</div>
                                  )}
                                </div>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Quick Actions - Only show when company is selected */}
        {currentCompany && (
          <SidebarGroup>
            <SidebarGroupLabel>Quick Actions</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <Button
                    onClick={handleNewChat}
                    className="w-full justify-start gap-2 relative"
                    variant="ghost"
                  >
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <Plus size={16} />
                    <span>New Chat</span>
                  </Button>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Settings */}
        <SidebarGroup>
          <SidebarGroupLabel>Settings</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.settings.map((item) => {
                const isActive = isActiveUrl(item.url);
                const ItemIcon = item.icon;
                
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive} tooltip={item.title}>
                      <Link to={item.url} className="flex items-center">
                        <ItemIcon className="mr-3 h-5 w-5" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <Button
              onClick={handleSignOut}
              variant="ghost"
              className="w-full justify-start gap-2 opacity-80 hover:opacity-100 transition-opacity"
            >
              <LogOut size={16} />
              <span>Sign Out</span>
            </Button>
          </SidebarMenuItem>
        </SidebarMenu>
        <div className="px-4 py-2">
          <VersionDisplay />
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}